import { apiClient } from './client';

/**
 * Client for `/api/clients/*` (ADR-046 — the Client CRM). Stays in lockstep
 * with `server/src/routes/clients/clients.routes.ts` + the module services.
 * The workspace needs `use_clients`; config (types) needs `manage_client_config`.
 */

// ── Shared DTOs (mirror the server contract) ──────────────────────────────

export type ClientLifecycleStage = 'lead' | 'qualified' | 'onboarding' | 'live' | 'dormant' | 'lost';

export const LIFECYCLE_STAGES: ClientLifecycleStage[] = [
  'lead',
  'qualified',
  'onboarding',
  'live',
  'dormant',
  'lost',
];

export interface ClientTypeView {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  contactRoleLabels: string[];
  sortOrder: number;
  active: boolean;
}

export interface ClientListItem {
  id: string;
  name: string;
  slug: string;
  lifecycleStage: ClientLifecycleStage;
  ownerMembershipId: string | null;
  tags: string[];
  dealValueMonthly: string | null;
  updatedAt: string;
  clientType: { id: string; name: string; slug: string };
  /** Link to the client's Brand in the kaerwell-onboarding app. */
  onboardingBrandId: string | null;
  /** Cached onboarding status rollup (synced from kaerwell-onboarding). */
  onboardingStatus: string | null;
}

export interface ClientContactView {
  id: string;
  clientId: string;
  email: string;
  firstName: string;
  lastName: string;
  title: string | null;
  phone: string | null;
  roleLabel: string | null;
  isPrimary: boolean;
  portalStatus: 'none' | 'invited' | 'active' | 'disabled';
  lastLoginAt: string | null;
}

export interface ClientDetail extends ClientListItem {
  source: string | null;
  emailDomains: string[];
  website: string | null;
  phone: string | null;
  notes: string | null;
  shipedgeClientKey: string | null;
  kwcOrgLinkId: string | null;
  convertedAt: string | null;
  liveAt: string | null;
  archivedAt: string | null;
  clientType: ClientListItem['clientType'] & { contactRoleLabels: string[] };
  contacts: ClientContactView[];
}

export interface ClientActivityView {
  id: string;
  clientId: string;
  type: string;
  actorType: 'staff' | 'client' | 'system';
  title: string;
  createdAt: string;
}

export interface Paged<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ClientListFilters {
  lifecycleStage?: ClientLifecycleStage[];
  clientTypeId?: string;
  ownerMembershipId?: string;
  tags?: string[];
  q?: string;
  includeArchived?: boolean;
  page?: number;
}

// ── Clients ────────────────────────────────────────────────────────────────

export async function listClients(filters: ClientListFilters = {}): Promise<Paged<ClientListItem>> {
  const params = new URLSearchParams();
  if (filters.lifecycleStage?.length) params.set('lifecycleStage', filters.lifecycleStage.join(','));
  if (filters.clientTypeId) params.set('clientTypeId', filters.clientTypeId);
  if (filters.ownerMembershipId) params.set('ownerMembershipId', filters.ownerMembershipId);
  if (filters.tags?.length) params.set('tags', filters.tags.join(','));
  if (filters.q) params.set('q', filters.q);
  if (filters.includeArchived) params.set('includeArchived', 'true');
  if (filters.page) params.set('page', String(filters.page));
  const qs = params.toString();
  const res = await apiClient.get<{ data: Paged<ClientListItem> }>(`/api/clients${qs ? `?${qs}` : ''}`);
  return res.data.data;
}

export async function getClient(id: string): Promise<ClientDetail> {
  const res = await apiClient.get<{ data: ClientDetail }>(`/api/clients/${encodeURIComponent(id)}`);
  return res.data.data;
}

export interface CreateClientBody {
  name: string;
  clientTypeId: string;
  lifecycleStage?: ClientLifecycleStage;
  source?: string | null;
  website?: string | null;
  phone?: string | null;
  tags?: string[];
  notes?: string | null;
  shipedgeClientKey?: string | null;
}

export async function createClient(body: CreateClientBody): Promise<ClientListItem> {
  const res = await apiClient.post<{ data: ClientListItem }>('/api/clients', body);
  return res.data.data;
}

export type UpdateClientBody = Partial<CreateClientBody> & { dealValueMonthly?: number | null };

export async function updateClient(id: string, body: UpdateClientBody): Promise<ClientListItem> {
  const res = await apiClient.patch<{ data: ClientListItem }>(`/api/clients/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

export async function archiveClient(id: string): Promise<void> {
  await apiClient.delete(`/api/clients/${encodeURIComponent(id)}`);
}

// ── Contacts ────────────────────────────────────────────────────────────────

export async function listContacts(clientId: string): Promise<ClientContactView[]> {
  const res = await apiClient.get<{ data: ClientContactView[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/contacts`,
  );
  return res.data.data;
}

export interface ContactBody {
  email: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  phone?: string | null;
  roleLabel?: string | null;
  isPrimary?: boolean;
}

export async function createContact(clientId: string, body: ContactBody): Promise<ClientContactView> {
  const res = await apiClient.post<{ data: ClientContactView }>(
    `/api/clients/${encodeURIComponent(clientId)}/contacts`,
    body,
  );
  return res.data.data;
}

export async function deleteContact(clientId: string, contactId: string): Promise<void> {
  await apiClient.delete(
    `/api/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
  );
}

// ── Timeline ────────────────────────────────────────────────────────────────

export async function listActivities(clientId: string, page = 1): Promise<Paged<ClientActivityView>> {
  const res = await apiClient.get<{ data: Paged<ClientActivityView> }>(
    `/api/clients/${encodeURIComponent(clientId)}/activities?page=${String(page)}`,
  );
  return res.data.data;
}

export async function addClientNote(clientId: string, title: string): Promise<void> {
  await apiClient.post(`/api/clients/${encodeURIComponent(clientId)}/activities`, { title });
}

// ── Client types (config) ────────────────────────────────────────────────────

export async function listClientTypes(): Promise<ClientTypeView[]> {
  const res = await apiClient.get<{ data: ClientTypeView[] }>('/api/clients/types');
  return res.data.data;
}
